/**
 * Resolves the base URL for API requests.
 *
 * Resolution order:
 *   1. `VITE_DIVEPEAKS_API_BASE_URL` when set — used for production / staging
 *      builds where the API lives on a fixed domain.
 *   2. Derived from the current host. The SPA is always served from
 *      `app.<domain>`, `gym.<domain>` or `admin.<domain>` and the API lives at
 *      `api.<domain>`, so we swap the first label. This makes every LAN host
 *      work with no config:
 *        app.divepeaks.local        -> api.divepeaks.local
 *        admin.divepeaks.local      -> api.divepeaks.local
 *        app.192.168.1.93.sslip.io  -> api.192.168.1.93.sslip.io
 *        app.192.168.1.28.sslip.io  -> api.192.168.1.28.sslip.io
 *      The API host always tracks whichever host the app was loaded from, so
 *      switching Wi-Fi networks (and thus LAN IP) needs no `.env` change.
 *   3. Fallback to `https://api.divepeaks.local` for hosts without one of those
 *      prefixes (e.g. direct `localhost` access).
 */

/**
 * The hosts this SPA is served from. `admin.` is the second name of the same
 * panel `gym.` serves (see main.tsx); leaving it out here would send the whole
 * panel to the fallback API host.
 */
const APP_LABELS = [ 'app', 'gym', 'admin' ];

export function getApiBaseUrl(): string {
  const explicit = import.meta.env.VITE_DIVEPEAKS_API_BASE_URL;
  if (explicit) return explicit;

  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location;
    const labels = hostname.split('.');
    if (labels.length > 1 && APP_LABELS.includes(labels[0])) {
      labels[0] = 'api';
      return `${protocol}//${labels.join('.')}`;
    }
  }

  return 'https://api.divepeaks.local';
}
